import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from '@zag/core/models';

export const shiftDefinitionStatuses = ['active', 'inactive'] as const;
export type ShiftDefinitionStatus = (typeof shiftDefinitionStatuses)[number];

const validWeekdaysSchema = type('number[]')
  .narrow((arr, ctx) => {
    if (arr.length > 7) return ctx.reject({ expected: 'at most 7 elements', actual: String(arr.length) });
    for (const d of arr) {
      if (!Number.isInteger(d) || d < 0 || d > 6) return ctx.reject({ expected: 'integer 0–6', actual: String(d) });
    }
    if (new Set(arr).size !== arr.length) return ctx.reject({ expected: 'unique elements', actual: 'duplicates found' });
    return true;
  })
  .configure({
    description: 'ISO weekday indices on which this shift is valid (0=Monday … 6=Sunday). Omit or pass [] for all days.',
    examples: [[0, 1, 2, 3, 4]],
  });

export const shiftDefinitionQuerySchema = type({
  'name?': 'string',
  'status?': type.enumerated(...shiftDefinitionStatuses),
});

export type ShiftDefinitionQuery = typeof shiftDefinitionQuerySchema.t;

export const SHIFT_DEFINITION_TRANSLATABLE_FIELDS = ['name'] as const;
export type ShiftDefinitionTranslatableField = (typeof SHIFT_DEFINITION_TRANSLATABLE_FIELDS)[number];
export type ShiftDefinitionTranslations = TranslatableText<ShiftDefinitionTranslatableField>;

export const createShiftDefinitionSchema = type({
  'name?': type('string').configure({
    description: 'Display name of the shift definition.',
    examples: ['Early', 'Late', 'Night'],
  }),
  'startTime?': type('string').configure({
    description: 'Start time as an `HH:mm` 24-hour clock string.',
    examples: ['06:00', '08:00'],
  }),
  'endTime?': type('string').configure({
    description: 'End time as an `HH:mm` 24-hour clock string.',
    examples: ['14:00', '16:30'],
  }),
  'breakMinutes?': type('number>=0 | null').configure({
    description: 'Unpaid break in minutes. Use `null` to clear the value.',
    examples: [30],
  }),
  'defaultRoleId?': type('string | null').configure({
    description: 'Default job-role ID applied to assignments. `null` clears the default.',
    examples: ['jobrole_abc123'],
  }),
  'requiredSkills?': type('unknown').configure({
    description: 'Skill requirements for the shift (array of skill IDs or structured requirement descriptor).',
    examples: [['skill_abc123', 'skill_def456']],
  }),
  'allowOverlap?': type('boolean').configure({
    description: 'When true, assignments derived from this definition may overlap in time.',
    examples: [false],
  }),
  'employeeOverridable?': type('boolean').configure({
    description: 'When true, employees assigned to this definition may edit their own shift time/location.',
    examples: [false],
  }),
  'locationId?': type('string | null').configure({
    description: 'Default location ID applied to shift units under this definition. `null` clears the default.',
    examples: ['location_abc123'],
  }),
  'status?': type.enumerated(...shiftDefinitionStatuses).configure({
    description: "Lifecycle status. 'active' definitions are available for planning; 'inactive' ones are hidden.",
    examples: ['active'],
  }),
  'icon?': type('string | null').configure({
    description: 'Material Icons identifier used in shift chips. `null` falls back to a default icon.',
    examples: ['wb_sunny'],
  }),
  'color?': type('string | null').configure({
    description: 'CSS accent colour for the shift, usually a six-digit hex. `null` falls back to the default palette.',
    examples: ['#0055aa'],
  }),
  'imageUrl?': type('string | null').configure({
    description: 'URL to an illustrative image for the shift. `null` clears the image.',
  }),
  'strategyId?': type('string').configure({
    description: 'Strategy to file the shift definition under. Defaults to the system shift-definition strategy.',
    examples: ['shift-definitions'],
  }),
  'capacity?': type('number>=0').configure({
    description: 'Standard number of staff required per occurrence of this shift. Used as the default capacity in plan templates.',
    examples: [1, 3],
  }),
  'balanceType?': type('string | null').configure({
    description: 'Balance type key this shift contributes to. `null` means no balance effect.',
    examples: ['overtime', null],
  }),
  'validWeekdays?': validWeekdaysSchema,
  'translations?': translatableTextSchema(SHIFT_DEFINITION_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations for `name`. When provided, `translations.en.name` is mirrored into the legacy column for back-compat.',
  }),
}).configure({ description: 'Payload for creating a reusable shift definition (template for shifts).' });

export interface CreateShiftDefinition {
  name?: string;
  startTime?: string;
  endTime?: string;
  breakMinutes?: number | null;
  defaultRoleId?: string | null;
  requiredSkills?: unknown;
  allowOverlap?: boolean;
  employeeOverridable?: boolean;
  locationId?: string | null;
  status?: ShiftDefinitionStatus;
  icon?: string | null;
  color?: string | null;
  imageUrl?: string | null;
  strategyId?: string;
  capacity?: number;
  balanceType?: string | null;
  validWeekdays?: number[];
  translations?: ShiftDefinitionTranslations;
}

export const updateShiftDefinitionSchema = type({
  'name?': type('string').configure({
    description: 'Updated display name.',
    examples: ['Early'],
  }),
  'startTime?': type('string').configure({
    description: 'Updated start time as `HH:mm`.',
    examples: ['06:00'],
  }),
  'endTime?': type('string').configure({
    description: 'Updated end time as `HH:mm`.',
    examples: ['14:00'],
  }),
  'breakMinutes?': type('number>=0 | null').configure({
    description: 'Updated unpaid break in minutes. Use `null` to clear.',
    examples: [30],
  }),
  'defaultRoleId?': type('string | null').configure({
    description: 'Updated default job-role ID. `null` clears the default.',
    examples: ['jobrole_abc123'],
  }),
  'requiredSkills?': type('unknown').configure({
    description: 'Updated skill requirements for assignments.',
    examples: [['skill_abc123']],
  }),
  'allowOverlap?': type('boolean').configure({
    description: 'Updated overlap allowance flag.',
    examples: [false],
  }),
  'employeeOverridable?': type('boolean').configure({
    description: 'Updated employee-owned time/location override flag.',
    examples: [false],
  }),
  'locationId?': type('string | null').configure({
    description: 'Updated default location ID for shift units. `null` clears the default.',
    examples: ['location_abc123'],
  }),
  'status?': type.enumerated(...shiftDefinitionStatuses).configure({
    description: "Updated lifecycle status ('active' or 'inactive').",
    examples: ['active'],
  }),
  'icon?': type('string | null').configure({
    description: 'Updated Material Icons identifier. `null` clears the icon.',
    examples: ['wb_sunny'],
  }),
  'color?': type('string | null').configure({
    description: 'Updated CSS accent colour. `null` clears the colour.',
    examples: ['#0055aa'],
  }),
  'imageUrl?': type('string | null').configure({
    description: 'Updated image URL. `null` clears the image.',
  }),
  'strategyId?': type('string').configure({
    description: 'Updated strategy ID — moves the shift definition to a different strategy.',
    examples: ['shift-definitions'],
  }),
  'capacity?': type('number>=0').configure({
    description: 'Updated standard capacity (staff required per occurrence). Applied to all shift units of this definition.',
    examples: [1, 3],
  }),
  'balanceType?': type('string | null').configure({
    description: 'Updated balance type key. `null` clears the balance effect for this shift.',
    examples: ['overtime', null],
  }),
  'validWeekdays?': validWeekdaysSchema,
  'translations?': translatableTextSchema(SHIFT_DEFINITION_TRANSLATABLE_FIELDS).configure({
    description: 'Per-language translations to merge onto existing ones. Deep-merges into existing translations.',
  }),
}).configure({ description: 'Partial update of a shift definition — fields omitted from the body remain unchanged.' });

export interface UpdateShiftDefinition {
  name?: string;
  startTime?: string;
  endTime?: string;
  breakMinutes?: number | null;
  defaultRoleId?: string | null;
  requiredSkills?: unknown;
  allowOverlap?: boolean;
  employeeOverridable?: boolean;
  locationId?: string | null;
  status?: ShiftDefinitionStatus;
  icon?: string | null;
  color?: string | null;
  imageUrl?: string | null;
  strategyId?: string;
  capacity?: number;
  balanceType?: string | null;
  validWeekdays?: number[];
  translations?: ShiftDefinitionTranslations;
}

/** Extract valid weekdays from a shift's `rules` JSONB. Returns [] when unset (= all days valid). */
export function extractValidWeekdays(rules: unknown): number[] {
  if (!rules || typeof rules !== 'object' || Array.isArray(rules)) return [];
  const raw = (rules as Record<string, unknown>)['weekday'];
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 6);
}

/**
 * Shift definition response from API
 */
export interface ShiftDefinitionResponse {
  id: string;
  strategyId: string;
  name: string | null;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number | null;
  defaultRoleId: string | null;
  allowOverlap: boolean;
  employeeOverridable: boolean;
  locationId: string | null;
  /** Standard capacity stored inside the `rules` JSONB; exposed as a top-level convenience field. */
  capacity: number;
  rules: unknown;
  status: ShiftDefinitionStatus;
  icon: string | null;
  color: string | null;
  imageUrl: string | null;
  /** Balance type key this shift contributes to. `null` means no balance effect. */
  balanceType: string | null;
  /** Valid ISO weekdays (0=Mon … 6=Sun). Empty array = all days valid. */
  validWeekdays: number[];
  createdAt: string;
  updatedAt: string;
  /** Full per-language translations map. Only populated when the caller asked for it via `?include=translations`. */
  translations?: TranslatableText<string>;
}

/**
 * @deprecated Use ShiftDefinitionResponse instead
 */
export type ShiftDefinition = ShiftDefinitionResponse;
